import React, { useState, useMemo, useEffect } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO, isBefore, isToday, addMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  TrendingUp, Plus, Search, Trash2, Edit2, Loader2, Calendar,
  AlertTriangle, CheckCircle2, BarChart3, Repeat, ArrowUpDown,
  CalendarDays, DollarSign, Clock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ContaReceberForm, { categoriasReceita } from "../components/contas/ContaReceberForm";
import CalendarioContas from "../components/contas/CalendarioContas";
import Paginacao from "../components/shared/Paginacao";

export default function ContasReceber() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  const urlParams = new URLSearchParams(window.location.search);
  const initialQ = urlParams.get('q') || "";
  const contaIdParam = urlParams.get('conta_id');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConta, setEditingConta] = useState(null);
  const [searchTerm, setSearchTerm] = useState(initialQ);
  const [filterStatus, setFilterStatus] = useState("pendentes");
  const [filterCategoria, setFilterCategoria] = useState("todas");
  const [sortBy, setSortBy] = useState("vencimento");
  const [activeTab, setActiveTab] = useState("lista");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [settleConta, setSettleConta] = useState(null);
  const [settleData, setSettleData] = useState({ juros: "0", multa: "0", desconto: "0", forma_recebimento: "pix", data_recebimento: new Date().toISOString().split('T')[0], conta_bancaria_id: "", observacao: "" });
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundConta, setRefundConta] = useState(null);
  const [refundMotivo, setRefundMotivo] = useState("");
  const [refundConfirm, setRefundConfirm] = useState(false);

  const queryClient = useQueryClient();

  const { data: contasReceber = [], isLoading } = useQuery({
    queryKey: ['contas_receber', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaReceber.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: contasBancarias = [] } = useQuery({
    queryKey: ['contas_bancarias', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaBancaria.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: lancamentos = [] } = useQuery({
    queryKey: ['lancamentos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Lancamento.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: contasPagar = [] } = useQuery({
    queryKey: ['contas_pagar', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaPagar.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  useEffect(() => {
    if (!contaIdParam || !contasReceber?.length) return;
    const c = contasReceber.find(x => String(x.id) === String(contaIdParam));
    if (c) { setEditingConta(c); setDialogOpen(true); }
  }, [contasReceber, contaIdParam]);

  const deleteMutation = useMutation({
    mutationFn: (id) => agrogestao.entities.ContaReceber.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contas_receber'] })
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => agrogestao.entities.ContaReceber.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contas_receber'] })
  });

  const getLancamentoRelacionadoReceber = (conta) => {
    if (!conta || conta.status !== 'recebido' || !conta.data_recebimento) return null;
    const liquido = (conta.valor || 0) + (conta.juros || 0) + (conta.multa || 0) - (conta.desconto || 0);
    const byRel = (lancamentos || []).find(l => String(l.conta_receber_id) === String(conta.id));
    if (byRel) return byRel;
    return (lancamentos || []).find(l =>
      l.tipo === 'receita' &&
      l.propriedade_id === propriedadeId &&
      Math.abs((l.valor || 0) - liquido) < 0.001 &&
      (l.descricao || '').startsWith(conta.descricao || '') &&
      (l.fornecedor_cliente || '') === (conta.cliente || '') &&
      l.data === conta.data_recebimento
    ) || null;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  // Processar status das contas
  const contasProcessadas = useMemo(() => {
    const hoje = new Date();
    return contasReceber.map(c => {
      const vencimento = parseISO(c.data_vencimento);
      const atrasada = c.status !== 'recebido' && isBefore(vencimento, hoje) && !isToday(vencimento);
      return {
        ...c,
        status: atrasada ? 'atrasado' : c.status,
        vencimento
      };
    });
  }, [contasReceber]);

  // Filtros e ordenação
  const contasFiltradas = useMemo(() => {
    let filtered = contasProcessadas;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.descricao?.toLowerCase().includes(term) ||
        c.cliente?.toLowerCase().includes(term)
      );
    }

    if (filterStatus === "pendentes") {
      filtered = filtered.filter(c => c.status !== 'recebido');
    } else if (filterStatus !== "todas") {
      filtered = filtered.filter(c => c.status === filterStatus);
    }

    if (filterCategoria !== "todas") {
      filtered = filtered.filter(c => c.categoria === filterCategoria);
    }

    filtered.sort((a, b) => {
      if (sortBy === "vencimento") return a.vencimento - b.vencimento;
      if (sortBy === "valor") return b.valor - a.valor;
      return 0;
    });

    return filtered;
  }, [contasProcessadas, searchTerm, filterStatus, filterCategoria, sortBy]);

  // Paginação
  const contasPaginadas = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return contasFiltradas.slice(start, start + pageSize);
  }, [contasFiltradas, currentPage, pageSize]);

  // Reset page quando filtros mudam
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterCategoria]);

  // Totais
  const totais = useMemo(() => {
    const pendentes = contasProcessadas.filter(c => c.status !== 'recebido');
    return {
      total: pendentes.reduce((acc, c) => acc + (c.valor || 0), 0),
      atrasadas: pendentes.filter(c => c.status === 'atrasado').reduce((acc, c) => acc + (c.valor || 0), 0),
      quantidade: pendentes.length,
      qtdAtrasadas: pendentes.filter(c => c.status === 'atrasado').length
    };
  }, [contasProcessadas]);

  const handleNew = () => {
    setEditingConta(null);
    setDialogOpen(true);
  };

  const handleEdit = (conta) => {
    if (conta.status === 'recebido') return;
    setEditingConta(conta);
    setDialogOpen(true);
  };

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['contas_receber'] });
    setDialogOpen(false);
    setEditingConta(null);
  };

  const handleMarcarRecebido = async (conta) => {
    const hoje = new Date().toISOString().split('T')[0]
    await updateMutation.mutateAsync({
      id: conta.id,
      data: {
        status: 'recebido',
        data_recebimento: hoje
      }
    });

    // Se for recorrente, criar próxima
    if (conta.recorrente && conta.frequencia_recorrencia) {
      const mesesAdicionar = {
        semanal: 0.25,
        quinzenal: 0.5,
        mensal: 1,
        bimestral: 2,
        trimestral: 3,
        semestral: 6,
        anual: 12
      };

      const meses = mesesAdicionar[conta.frequencia_recorrencia] || 1;
      const novaData = addMonths(conta.vencimento, meses);

      await agrogestao.entities.ContaReceber.create({
        propriedade_id: conta.propriedade_id,
        descricao: conta.descricao,
        valor: conta.valor,
        data_vencimento: format(novaData, 'yyyy-MM-dd'),
        categoria: conta.categoria,
        cliente: conta.cliente,
        status: 'pendente',
        recorrente: true,
        frequencia_recorrencia: conta.frequencia_recorrencia,
        lembrete_dias: conta.lembrete_dias,
        forma_recebimento: conta.forma_recebimento,
        safra_id: conta.safra_id
      });

      queryClient.invalidateQueries({ queryKey: ['contas_receber'] });
    }
  };

  const abrirRecebimento = (conta) => {
    setSettleConta(conta);
    setSettleData({
      juros: "0",
      multa: "0",
      desconto: "0",
      forma_recebimento: conta.forma_recebimento || "pix",
      data_recebimento: new Date().toISOString().split('T')[0],
      conta_bancaria_id: "",
      observacao: ""
    });
    setSettleDialogOpen(true);
  };

  const confirmarRecebimento = async () => {
    if (!settleConta) return;
    const juros = parseFloat(settleData.juros) || 0;
    const multa = parseFloat(settleData.multa) || 0;
    const desconto = parseFloat(settleData.desconto) || 0;
    const dataRec = settleData.data_recebimento || new Date().toISOString().split('T')[0];

    await updateMutation.mutateAsync({
      id: settleConta.id,
      data: {
        status: 'recebido',
        data_recebimento: dataRec,
        juros,
        multa,
        desconto,
        conta_bancaria_id: settleData.conta_bancaria_id || null,
        forma_recebimento: settleData.forma_recebimento
      }
    });

    const totalRecebido = (settleConta.valor || 0) + juros + multa - desconto;

    // AUTOMATIC BANK SETTLEMENT LOGIC
    if (settleData.conta_bancaria_id) {
      try {
        const contasBanco = await agrogestao.entities.ContaBancaria.filter({ id: settleData.conta_bancaria_id });
        const contaBanco = contasBanco[0];

        if (contaBanco) {
          // 1. Update Balance (Receipt = Credit, so ADD)
          const novoSaldo = (contaBanco.saldo_atual || 0) + totalRecebido;
          await agrogestao.entities.ContaBancaria.update(contaBanco.id, { saldo_atual: novoSaldo });

          // 2. Create Statement Entry
          await agrogestao.entities.ExtratoBancario.create({
            propriedade_id: settleConta.propriedade_id,
            conta_bancaria_id: contaBanco.id,
            conta_receber_id: settleConta.id,
            data: dataRec,
            descricao: `Recebimento: ${settleConta.descricao} (${settleConta.cliente || ''})`,
            valor: totalRecebido,
            tipo: 'credito'
          });
        }
      } catch (error) {
        console.error("Erro ao atualizar saldo bancário", error);
      }
    }

    setSettleDialogOpen(false);
    setSettleConta(null);
    queryClient.invalidateQueries({ queryKey: ['contas_receber'] });
    queryClient.invalidateQueries({ queryKey: ['lancamentos'] });
  };

  const getStatusBadge = (status) => {
    const config = {
      recebido: { label: "Recebido", class: "bg-green-100 text-green-700" },
      pendente: { label: "Pendente", class: "bg-blue-100 text-blue-700" },
      atrasado: { label: "Atrasado", class: "bg-red-100 text-red-700" },
      agendado: { label: "Agendado", class: "bg-purple-100 text-purple-700" }
    };
    const c = config[status] || config.pendente;
    return <Badge className={c.class}>{c.label}</Badge>;
  };

  const getCategoriaLabel = (cat) => categoriasReceita.find(c => c.value === cat)?.label || cat;
  const getCategoriaIcon = (cat) => categoriasReceita.find(c => c.value === cat)?.icon || "📦";

  if (!propriedadeId) {
    return (
      <div className="text-center py-20">
        <p className="text-stone-600">Selecione uma propriedade</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800 flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-green-600" />
            Contas a Receber
          </h1>
          <p className="text-stone-600 mt-1">Gerencie suas receitas esperadas</p>
        </div>

        <Button onClick={handleNew} className="bg-green-600 hover:bg-green-700 gap-2">
          <Plus className="w-5 h-5" />
          Nova Receita
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <p className="text-green-100 text-sm">Total a Receber</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(totais.total)}</p>
            <p className="text-xs text-green-200 mt-1">{totais.quantidade} receita(s)</p>
          </CardContent>
        </Card>

        <Card className={`${totais.atrasadas > 0 ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-gradient-to-br from-emerald-500 to-emerald-600'} text-white`}>
          <CardContent className="p-4">
            <p className="text-white/80 text-sm">Atrasadas</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(totais.atrasadas)}</p>
            <p className="text-xs text-white/70 mt-1">{totais.qtdAtrasadas} receita(s)</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#2D6A4F] to-[#1B4332] text-white">
          <CardContent className="p-4">
            <p className="text-white/70 text-sm">Recebido (mês)</p>
            <p className="text-2xl font-bold mt-1">
              {formatCurrency(contasProcessadas.filter(c => c.status === 'recebido').reduce((acc, c) => acc + (c.valor || 0), 0))}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <p className="text-indigo-100 text-sm">Saldo Projetado</p>
            <p className="text-2xl font-bold mt-1">
              {formatCurrency(totais.total - contasPagar.filter(c => c.status !== 'pago').reduce((acc, c) => acc + (c.valor || 0), 0))}
            </p>
            <p className="text-xs text-indigo-200 mt-1">Receitas - Despesas</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-stone-100 p-1 flex-wrap h-auto">
          <TabsTrigger value="lista" className="gap-2">
            <DollarSign className="w-4 h-4" />
            Lista
          </TabsTrigger>
          <TabsTrigger value="calendario" className="gap-2">
            <CalendarDays className="w-4 h-4" />
            Calendário
          </TabsTrigger>
          <TabsTrigger value="lembretes" className="gap-2">
            <Clock className="w-4 h-4" />
            Lembretes
          </TabsTrigger>
        </TabsList>

        {/* Lista */}
        <TabsContent value="lista" className="mt-6 space-y-4">
          <div className="bg-white rounded-xl border border-stone-200 p-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                <Input
                  placeholder="Buscar por descrição ou cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendentes">Pendentes</SelectItem>
                    <SelectItem value="todas">Todas</SelectItem>
                    <SelectItem value="atrasado">Atrasadas</SelectItem>
                    <SelectItem value="recebido">Recebidas</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    {categoriasReceita.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-36">
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vencimento">Data</SelectItem>
                    <SelectItem value="valor">Valor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
            {contasFiltradas.length === 0 ? (
              <div className="p-12 text-center">
                <TrendingUp className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">Nenhuma receita encontrada</p>
              </div>
            ) : (
              <>
                <div className="p-3 border-b border-stone-100 text-sm text-stone-500">{contasFiltradas.length} receita(s) encontrada(s)</div>
                <div className="divide-y divide-stone-100">
                  <AnimatePresence>
                    {contasPaginadas.map((conta, index) => (
                      <motion.div
                        key={conta.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ delay: index * 0.02 }}
                        className={`p-4 hover:bg-stone-50 transition-colors ${conta.status === 'atrasado' ? 'bg-amber-50/50' : ''}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="text-2xl">{getCategoriaIcon(conta.categoria)}</div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-stone-800 truncate" title={conta.descricao}>{conta.descricao}</p>
                              {getStatusBadge(conta.status)}
                              {conta.recorrente && (
                                <Badge variant="outline" className="gap-1">
                                  <Repeat className="w-3 h-3" />
                                  {conta.frequencia_recorrencia}
                                </Badge>
                              )}
                              {conta.parcela_atual && conta.total_parcelas && (
                                <Badge variant="secondary">
                                  {conta.parcela_atual}/{conta.total_parcelas}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-stone-500 mt-1">
                              {getCategoriaLabel(conta.categoria)}
                              {conta.cliente && ` • ${conta.cliente}`}
                            </p>
                          </div>

                          <div className="text-right">
                            <p className="text-lg font-bold text-green-600">+{formatCurrency(conta.valor)}</p>
                            {conta.status === 'recebido' ? (
                              <p className="text-sm text-green-700">
                                <Calendar className="w-3 h-3 inline mr-1" />
                                Recebido em {format(parseISO(conta.data_recebimento || conta.data_vencimento), "dd/MM/yyyy")}
                              </p>
                            ) : (
                              <p className={`text-sm ${conta.status === 'atrasado' ? 'text-amber-600 font-medium' : 'text-stone-500'}`}>
                                <Calendar className="w-3 h-3 inline mr-1" />
                                Vence em {format(conta.vencimento, "dd/MM/yyyy")}
                              </p>
                            )}
                            {conta.status === 'recebido' && (
                              <div className="mt-1 flex justify-end gap-1">
                                {conta.forma_recebimento && (
                                  <Badge variant="outline" className="text-xs">{conta.forma_recebimento}</Badge>
                                )}
                                {conta.conta_bancaria_id && (() => {
                                  const cb = contasBancarias.find(cb => String(cb.id) === String(conta.conta_bancaria_id));
                                  return cb ? <Badge variant="outline" className="text-xs">{cb.banco} • {cb.numero}</Badge> : null;
                                })()}
                              </div>
                            )}
                          </div>

                          <div className="flex gap-1">
                            {conta.status !== 'recebido' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => abrirRecebimento(conta)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </Button>
                            )}
                            {conta.status === 'recebido' && (() => {
                              const rel = getLancamentoRelacionadoReceber(conta);
                              return (
                                <>
                                  {rel ? (
                                    <Link
                                      to={`${createPageUrl('Financeiro')}?lancamento_id=${rel.id}`}
                                      className="text-[#40916C] text-sm px-2 py-1 hover:text-[#2D6A4F]"
                                      title="Ver lançamento"
                                    >
                                      Ver lançamento
                                    </Link>
                                  ) : null}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setRefundConta(conta); setRefundMotivo(""); setRefundConfirm(false); setRefundDialogOpen(true); }}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  >
                                    Estornar
                                  </Button>
                                </>
                              );
                            })()}
                            {conta.status !== 'recebido' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(conta)}
                                className="text-stone-400 hover:text-stone-600"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                            )}
                            {conta.status !== 'recebido' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteMutation.mutate(conta.id)}
                                className="text-stone-400 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          {conta.status === 'recebido' && (
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                              <Badge variant="outline" className="bg-stone-50 text-stone-700">Juros: {formatCurrency(conta.juros || 0)}</Badge>
                              <Badge variant="outline" className="bg-stone-50 text-stone-700">Multa: {formatCurrency(conta.multa || 0)}</Badge>
                              <Badge variant="outline" className="bg-stone-50 text-stone-700">Desconto: {formatCurrency(conta.desconto || 0)}</Badge>
                              <Badge variant="outline" className="bg-stone-50 text-stone-700">Original: {formatCurrency(conta.valor || 0)}</Badge>
                              <Badge className="bg-green-100 text-green-700">Total Recebido: {formatCurrency((conta.valor || 0) + (conta.juros || 0) + (conta.multa || 0) - (conta.desconto || 0))}</Badge>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
                <Paginacao
                  totalItems={contasFiltradas.length}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
                />
              </>
            )}
          </div>
        </TabsContent>

        {/* Calendário */}
        <TabsContent value="calendario" className="mt-6">
          <CalendarioContas
            contasPagar={contasPagar}
            contasReceber={contasProcessadas}
          />
        </TabsContent>

        {/* Lembretes */}
        <TabsContent value="lembretes" className="mt-6">
          <LembretesReceber
            contas={contasProcessadas}
            onMarcarRecebido={handleMarcarRecebido}
          />
        </TabsContent>
      </Tabs>

      {/* Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              {editingConta ? "Editar Receita" : "Nova Receita a Receber"}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados da receita a receber
            </DialogDescription>
          </DialogHeader>
          <ContaReceberForm
            conta={editingConta}
            onSuccess={handleSuccess}
            onCancel={() => setDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={refundDialogOpen} onOpenChange={(v) => { setRefundDialogOpen(v); if (!v) setRefundConta(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Estornar recebimento</DialogTitle>
            <DialogDescription>Esta operação é crítica. Preencha o motivo e confirme.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Motivo do estorno</Label>
              <Textarea rows={3} placeholder="Descreva o motivo" value={refundMotivo} onChange={(e) => setRefundMotivo(e.target.value)} />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="refundConfirmRec" className="w-4 h-4" checked={refundConfirm} onChange={(e) => setRefundConfirm(e.target.checked)} />
              <label htmlFor="refundConfirmRec" className="text-sm text-stone-600">Confirmo o estorno deste recebimento</label>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setRefundDialogOpen(false); setRefundConta(null); }} className="flex-1">Cancelar</Button>
            <Button
              onClick={async () => {
                if (!refundConfirm || !refundConta) return;
                try {
                  const perm = await agrogestao.functions.invoke('validarAcessoPropriedade', {
                    propriedade_id: refundConta.propriedade_id || localStorage.getItem('propriedade_selecionada'),
                    acao: 'financeiro_estorno'
                  });
                  if (!perm?.data?.success) { return; }
                  const rel = getLancamentoRelacionadoReceber(refundConta);
                  if (rel) {
                    await agrogestao.entities.Lancamento.delete(rel.id);
                  }
                  await updateMutation.mutateAsync({ id: refundConta.id, data: { status: 'pendente', data_recebimento: null, juros: 0, multa: 0, desconto: 0 } });
                  try {
                    const extratos = await agrogestao.entities.ExtratoBancario.filter({ propriedade_id: refundConta.propriedade_id || propriedadeId });
                    const vinculados = (extratos || []).filter(e => String(e.conta_receber_id) === String(refundConta.id) || String(e.lancamento_id) === String(rel?.id));
                    for (const e of vinculados) {
                      if (e.conta_bancaria_id) {
                        const contasBanco = await agrogestao.entities.ContaBancaria.filter({ id: e.conta_bancaria_id });
                        const contaBanco = contasBanco[0];
                        if (contaBanco) {
                          const novoSaldo = e.tipo === 'credito'
                            ? (contaBanco.saldo_atual || 0) - (e.valor || 0)
                            : (contaBanco.saldo_atual || 0) + (e.valor || 0);
                          await agrogestao.entities.ContaBancaria.update(contaBanco.id, { saldo_atual: novoSaldo });
                        }
                      }
                      await agrogestao.entities.ExtratoBancario.delete(e.id);
                    }
                  } catch { }
                  const u = await agrogestao.auth.me();
                  await agrogestao.client.from('audit_logs').insert({
                    user_email: u?.email || null,
                    propriedade_id: refundConta.propriedade_id || localStorage.getItem('propriedade_selecionada') || null,
                    acao: 'estorno_baixa',
                    table: 'contas_receber',
                    status: 'success',
                    details: { conta_id: refundConta.id, motivo: refundMotivo, original: { valor: refundConta.valor, juros: refundConta.juros, multa: refundConta.multa, desconto: refundConta.desconto, data_recebimento: refundConta.data_recebimento } }
                  });
                  queryClient.invalidateQueries({ queryKey: ['contas_receber'] });
                  queryClient.invalidateQueries({ queryKey: ['lancamentos'] });
                  queryClient.invalidateQueries({ queryKey: ['extratos'] });
                  setRefundDialogOpen(false);
                  setRefundConta(null);
                } catch { }
              }}
              className="flex-1 bg-red-600 hover:bg-red-700"
              disabled={!refundConfirm || !refundMotivo}
            >
              Confirmar estorno
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={settleDialogOpen} onOpenChange={(v) => { setSettleDialogOpen(v); if (!v) setSettleConta(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Receber Receita</DialogTitle>
            <DialogDescription>Informe ajustes e finalize o recebimento</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Juros (R$)</Label>
              <Input type="number" step="0.01" value={settleData.juros} onChange={(e) => setSettleData({ ...settleData, juros: e.target.value })} />
            </div>
            <div>
              <Label>Multa (R$)</Label>
              <Input type="number" step="0.01" value={settleData.multa} onChange={(e) => setSettleData({ ...settleData, multa: e.target.value })} />
            </div>
            <div>
              <Label>Desconto (R$)</Label>
              <Input type="number" step="0.01" value={settleData.desconto} onChange={(e) => setSettleData({ ...settleData, desconto: e.target.value })} />
            </div>
            <div>
              <Label>Forma de Recebimento</Label>
              <Select value={settleData.forma_recebimento} onValueChange={(v) => setSettleData({ ...settleData, forma_recebimento: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="transferencia">Transferência</SelectItem>
                  <SelectItem value="deposito">Depósito</SelectItem>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="cartao">Cartão</SelectItem>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Conta Bancária</Label>
              <Select value={settleData.conta_bancaria_id} onValueChange={(v) => setSettleData({ ...settleData, conta_bancaria_id: v === 'none' ? '' : v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma</SelectItem>
                  {contasBancarias.map(cb => (
                    <SelectItem key={cb.id} value={cb.id}>{cb.banco} • {cb.numero}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Data do Recebimento</Label>
              <Input type="date" value={settleData.data_recebimento} onChange={(e) => setSettleData({ ...settleData, data_recebimento: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Observação</Label>
              <Textarea rows={2} placeholder="Observações do recebimento" value={settleData.observacao} onChange={(e) => setSettleData({ ...settleData, observacao: e.target.value })} />
            </div>
          </div>
          <div className="mt-4 p-3 bg-stone-50 rounded-xl border border-stone-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-stone-600">Valor original</p>
              <p className="font-semibold text-stone-800">{formatCurrency(settleConta?.valor || 0)}</p>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-sm text-stone-600">Total a receber</p>
              <p className="text-lg font-bold text-green-700">
                {formatCurrency((settleConta?.valor || 0) + (parseFloat(settleData.juros) || 0) + (parseFloat(settleData.multa) || 0) - (parseFloat(settleData.desconto) || 0))}
              </p>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => { setSettleDialogOpen(false); setSettleConta(null); }} className="flex-1">Cancelar</Button>
            <Button onClick={confirmarRecebimento} className="flex-1 bg-green-600 hover:bg-green-700">Confirmar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Componente de Lembretes inline
function LembretesReceber({ contas, onMarcarRecebido }) {
  const hoje = new Date();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const contasComLembrete = contas
    .filter(c => c.status !== 'recebido')
    .map(c => {
      const vencimento = parseISO(c.data_vencimento);
      const diasParaVencer = Math.ceil((vencimento - hoje) / (1000 * 60 * 60 * 24));
      const atrasada = isBefore(vencimento, hoje) && !isToday(vencimento);
      const venceHoje = isToday(vencimento);
      const dentroDoLembrete = diasParaVencer <= (c.lembrete_dias || 3) && diasParaVencer >= 0;

      return { ...c, vencimento, diasParaVencer, atrasada, venceHoje, dentroDoLembrete };
    })
    .filter(c => c.atrasada || c.venceHoje || c.dentroDoLembrete)
    .sort((a, b) => a.diasParaVencer - b.diasParaVencer);

  if (contasComLembrete.length === 0) {
    return (
      <Card className="bg-green-50 border-green-200">
        <CardContent className="p-6 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h3 className="font-bold text-green-800">Tudo em dia!</h3>
          <p className="text-sm text-green-600 mt-1">Nenhuma receita próxima do vencimento</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-stone-200">
      <CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-stone-800 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Receitas Pendentes ({contasComLembrete.length})
        </h3>

        {contasComLembrete.map(conta => (
          <div
            key={conta.id}
            className={`p-4 rounded-xl border ${conta.atrasada ? 'bg-red-50 border-red-200' :
              conta.venceHoje ? 'bg-amber-50 border-amber-200' :
                'bg-blue-50 border-blue-200'
              }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-stone-800">{conta.descricao}</p>
                <p className="text-sm text-stone-500">{conta.cliente}</p>
                <Badge className={`mt-2 ${conta.atrasada ? 'bg-red-100 text-red-700' :
                  conta.venceHoje ? 'bg-amber-100 text-amber-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                  {conta.atrasada ? `${Math.abs(conta.diasParaVencer)} dias atrasada` :
                    conta.venceHoje ? 'Vence hoje!' :
                      `Vence em ${conta.diasParaVencer} dias`}
                </Badge>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-green-600">+{formatCurrency(conta.valor)}</p>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => onMarcarRecebido(conta)}>
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Recebido
                </Button>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
